import React from 'react'
import ReactModal from 'react-modal'
import { ReactComponent as CloseModalIcon } from '../../../assets/svg/CloseModalIcon.svg'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { studentPulseStats, pulseStatus } from '../../../constants/PulseCheckAnalyticsConstants'
import { ANALYTICS_EVENTS } from '../../../analytics/events'
import useTrackedSurface from '../../../analytics/useTrackedSurface'

const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'risk', label: 'At risk' },
    { key: 'declining', label: 'Declining' },
    { key: 'watch', label: 'Watch' },
    { key: 'ok', label: 'Stable' },
]

const SORTS = [
    { key: 'recent_asc', label: 'Lowest score first' },
    { key: 'delta_asc', label: 'Biggest decline first' },
    { key: 'name_asc', label: 'Name A→Z' },
    { key: 'latest_desc', label: 'Highest score first' },
]

function StudentListModal({ modalOpen, closeModal, students = [] }) {
    const { t } = useTranslation()
    const history = useHistory()
    const [tab, setTab] = React.useState('all')
    const [q, setQ] = React.useState('')
    const [sort, setSort] = React.useState('recent_asc')
    useTrackedSurface(ANALYTICS_EVENTS.ANALYTICS_PULSE_STUDENT_LIST_VIEWED, {
        active: modalOpen,
        contentId: tab,
        content: FILTER_TABS.find((item) => item.key === tab)?.label,
    })

    const all = React.useMemo(() => students.map(studentPulseStats), [students])

    const counts = React.useMemo(() => {
        const c = { all: all.length, risk: 0, declining: 0, watch: 0, ok: 0 }
        all.forEach((s) => {
            const key = pulseStatus(s).key
            if (c[key] !== undefined) c[key]++
        })
        return c
    }, [all])

    const filtered = React.useMemo(() => {
        let r = all
        if (tab !== 'all') r = r.filter((s) => pulseStatus(s).key === tab)
        if (q.trim()) {
            const needle = q.toLowerCase()
            r = r.filter((s) => (s.name || '').toLowerCase().includes(needle) || (s.year || '').toLowerCase().includes(needle))
        }
        const sorters = {
            recent_asc: (a, b) => (a.recent ?? 99) - (b.recent ?? 99),
            delta_asc: (a, b) => a.delta - b.delta,
            name_asc: (a, b) => (a.name || '').localeCompare(b.name || ''),
            latest_desc: (a, b) => (b.latest ?? -1) - (a.latest ?? -1),
        }
        return [...r].sort(sorters[sort])
    }, [all, tab, q, sort])

    React.useEffect(() => {
        if (!modalOpen) return
        const onKey = (e) => e.key === 'Escape' && closeModal()
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [modalOpen, closeModal])

    if (!modalOpen) return null

    const openStudent = (id) => {
        if (!id) return
        closeModal()
        history.push(`/analytics/student?id=${id}&focus=pulse`)
    }

    return (
        <ReactModal
            transparent
            closeTimeoutMS={600}
            isOpen={modalOpen}
            onRequestClose={closeModal}
            className="noScroll"
            style={{
                overlay: { zIndex: 100, backgroundColor: 'rgba(0, 0, 0, 0.3)', backdropFilter: 'blur(5px)' },
                content: {
                    padding: 0,
                    opacity: 1,
                    width: 'min(1180px, 100%)',
                    margin: 'auto',
                    backgroundColor: 'transparent',
                    maxHeight: '100%',
                    border: 'none',
                    boxShadow: 'none',
                    overflow: 'scroll',
                    paddingTop: 35,
                    paddingBottom: 35,
                },
            }}
        >
            <div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div
                        onClick={closeModal}
                        className="w-[60px] h-[60px] bg-[#5E639B] rounded-full cursor-pointer flex items-center justify-center z-[2] transition-all duration-150 ease-in-out hover:bg-[#413F5E]"
                    >
                        <CloseModalIcon />
                    </div>
                </div>

                <div className="rounded-[18px] overflow-hidden bg-white mt-[-30px] shadow-[0_30px_80px_rgba(45,43,69,0.25)]">
                    {/* Header band */}
                    <div className="bg-[#F4F3FA] pt-[22px] px-[28px] pb-[18px]">
                        <div className="font-bold text-[18px] text-[#413F5E] mb-[4px]">{t('Student list')}</div>
                        <div className="font-medium text-[12px] text-[#9392AD] mb-[14px]">{t('Select a student to open their analytics')}</div>
                        {/* Search + sort row */}
                        <div className="flex gap-3 items-center flex-wrap">
                            <div className="flex-1 min-w-[240px] flex items-center gap-[10px] py-[11px] px-[18px] bg-white border border-[#ECEBF8] rounded-full">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9392AD" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="7" />
                                    <line x1="21" y1="21" x2="16.5" y2="16.5" />
                                </svg>
                                <input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder={t('search')}
                                    className="flex-1 border-none outline-none bg-transparent font-semibold text-[13px] text-[#413F5E]"
                                />
                            </div>
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                className="py-[11px] px-[18px] border-none rounded-full bg-[#5E639B] text-white font-bold text-[13px] cursor-pointer shadow-[0_6px_18px_rgba(94,99,155,0.25)]"
                            >
                                {SORTS.map((s) => (
                                    <option key={s.key} value={s.key} style={{ color: '#413F5E' }}>
                                        {t('Filter')}: {t(s.label)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* Status chip row */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
                            {FILTER_TABS.map((f) => {
                                const active = tab === f.key
                                const dot = { all: '#9392AD', risk: '#FC4C59', declining: '#F08A2A', watch: '#E0B62C', ok: '#3FA862' }[f.key]
                                return (
                                    <button
                                        key={f.key}
                                        onClick={() => setTab(f.key)}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '7px 14px',
                                            borderRadius: 99,
                                            cursor: 'pointer',
                                            border: active ? '1px solid #5E639B' : '1px solid #ECEBF8',
                                            background: active ? '#5E639B' : '#fff',
                                            color: active ? '#fff' : '#413F5E',
                                            fontFamily: 'Quicksand',
                                            fontWeight: 700,
                                            fontSize: 12,
                                        }}
                                    >
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
                                        {t(f.label)}{' '}
                                        <span
                                            style={{
                                                fontWeight: 700,
                                                fontSize: 11,
                                                background: active ? 'rgba(255,255,255,0.2)' : '#F4F3FA',
                                                color: active ? '#fff' : '#9392AD',
                                                padding: '1px 7px',
                                                borderRadius: 99,
                                            }}
                                        >
                                            {counts[f.key]}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {/* Body */}
                    <div className="bg-white pt-[20px] px-[28px] pb-[28px]">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                            <div style={{ fontFamily: 'Quicksand', fontWeight: 500, fontSize: 12, color: '#9392AD' }}>
                                {t('Showing')}{' '}
                                <span style={{ color: '#413F5E', fontWeight: 700 }}>
                                    {filtered.length} / {all.length}
                                </span>{' '}
                                {t('students')}
                            </div>
                        </div>

                        {filtered.length === 0 && (
                            <div style={{ padding: '60px 20px', textAlign: 'center', fontFamily: 'Quicksand', fontWeight: 600, fontSize: 14, color: '#9392AD' }}>
                                {t('No students match this filter.')}
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {filtered.map((s) => {
                                const st = pulseStatus(s)
                                const up = s.delta >= 0
                                const series = s.series.filter((v) => v !== null)
                                return (
                                    <div
                                        key={s.id || s.name}
                                        onClick={() => openStudent(s.id)}
                                        title={t('Open student analytics')}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'auto 1.4fr 0.8fr 0.7fr 0.7fr 1fr 0.9fr 36px',
                                            gap: 14,
                                            alignItems: 'center',
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            background: '#F8F8FB',
                                            border: '1px solid #ECEBF8',
                                            cursor: s.id ? 'pointer' : 'default',
                                        }}
                                    >
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, display: 'inline-block' }} />
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontFamily: 'Quicksand', fontWeight: 700, fontSize: 14, color: '#5E639B' }}>{s.name}</div>
                                            <div style={{ fontFamily: 'Quicksand', fontWeight: 500, fontSize: 11, color: '#9392AD' }}>
                                                {t('3-week avg')} {s.recent != null ? `${s.recent.toFixed(1)}/5` : '—'}
                                            </div>
                                        </div>
                                        <div style={{ fontFamily: 'Quicksand', fontWeight: 600, fontSize: 12, color: '#9392AD' }}>{s.year || '—'}</div>
                                        <div style={{ fontFamily: 'Quicksand', fontWeight: 700, fontSize: 16, color: '#413F5E' }}>
                                            {s.latest != null ? s.latest.toFixed(1) : '—'}
                                            <span style={{ fontSize: 10, color: '#9392AD', marginLeft: 2 }}>/5</span>
                                        </div>
                                        <div
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                fontFamily: 'Quicksand',
                                                fontWeight: 700,
                                                fontSize: 12,
                                                color: up ? '#2B8C50' : '#C73340',
                                            }}
                                        >
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                                                {up ? <polyline points="6 15 12 9 18 15" /> : <polyline points="6 9 12 15 18 9" />}
                                            </svg>
                                            {up ? '+' : ''}
                                            {s.delta.toFixed(1)}
                                        </div>
                                        {series.length > 1 ? <StudentSparkline data={series} color={st.dot} w={110} h={26} /> : <div />}
                                        <span
                                            style={{
                                                justifySelf: 'start',
                                                padding: '3px 11px',
                                                borderRadius: 99,
                                                background: st.bg,
                                                color: st.fg,
                                                fontFamily: 'Quicksand',
                                                fontWeight: 700,
                                                fontSize: 10,
                                            }}
                                        >
                                            {t(st.label)}
                                        </span>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#5E639B" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ justifySelf: 'end' }}>
                                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </ReactModal>
    )
}

export default StudentListModal

const StudentSparkline = ({ data, color = '#5E639B', w = 110, h = 28 }) => {
    const min = 1
    const max = 5
    const stepX = w / (data.length - 1)
    const pts = data.map((v, i) => [i * stepX, h - ((v - min) / (max - min)) * (h - 6) - 3])
    const path = pts.reduce((acc, p, i, a) => {
        if (i === 0) return `M ${p[0]} ${p[1]}`
        const prev = a[i - 1]
        const cx = prev[0] + (p[0] - prev[0]) / 2
        return acc + ` C ${cx} ${prev[1]}, ${cx} ${p[1]}, ${p[0]} ${p[1]}`
    }, '')
    const last = pts[pts.length - 1]
    return (
        <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
            <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
            <circle cx={last[0]} cy={last[1]} r="2.6" fill="#fff" stroke={color} strokeWidth="1.8" />
        </svg>
    )
}
